import type { DesignerNodeData } from "../../schemas/index.ts";
import type { GetRuntimeManifest } from "../../types/index.ts";
import type { GetDesignerNodeDataById } from "./getDesignerNodeData.ts";
import type { GetNodeSlotId } from "./getNodeSlotId.ts";

export const resolveActiveNodesDataCreate = (
  getRuntimeManifest: GetRuntimeManifest,
  getSlotId: GetNodeSlotId,
  getNodeById: GetDesignerNodeDataById,
) => {
  type Context = { callingParentId?: number; callingChildId?: number };

  const resolveActiveNodes = (
    nodeOrId: number | DesignerNodeData,
    { callingParentId, callingChildId }: Context = {},
  ) => {
    const node = typeof nodeOrId === "number" ? getNodeById(nodeOrId) : nodeOrId;
    const manifest = getRuntimeManifest(node.typeId);

    const entries: DesignerNodeData[] = [];

    const parentId = getSlotId(node.id)?.parentId;
    if (parentId && parentId !== callingParentId) {
      entries.push(...resolveActiveNodes(parentId, { callingChildId: node.id }));
    }

    // if called from parent, parent will have added it
    if (!callingParentId) {
      entries.push(node);
    }

    // NOTE limitations here currently:
    // 1. no support for publish-only children (child must exist at design time)
    // 2. active (runtime) child name assumed to match player-design-time child name
    if (manifest.children && Object.keys(node.childNodes).length > 0) {
      const activeChildren = Object.entries(manifest.children)
        .filter(([, childDescr]) => !!childDescr.alwaysActive) // this prop is runtime saying it will always be there
        .flatMap(([name, { alwaysActive }]) => {
          const entry = node.childNodes[name];
          const childEntries = Array.isArray(entry) ? entry : entry ? [entry] : [];
          if (alwaysActive === "when-singular" && childEntries.length > 1) {
            return [];
          }
          return childEntries;
        });

      const includedChildren = activeChildren.filter(({ id }) => id !== callingChildId);
      entries.push(...includedChildren); // add immediate child first to get top down ordering

      const context: Context = { callingParentId: node.id };
      for (const child of includedChildren) {
        entries.push(...resolveActiveNodes(child, context));
      }
    }

    return entries;
  };

  return resolveActiveNodes;
};

export type ResolveActiveNodesData = ReturnType<typeof resolveActiveNodesDataCreate>;
